import { getCookie } from "helpers/utils/cookie";
import { OffersDataType } from "wrapper/OffersWrapper/@types/offersContext";
import { logger } from "logging";
import { getStringFromId } from "shared/helpers/vernacular";
import { VERNACULAR_STRING_IDS } from "constants/vernacularConstants";
import { ACCORDIAN, DEVICE_TYPE, platformNames } from "shared/constants";
import { localStorageService } from "service/storage";
import { PAYMENT_METHODS } from "constants/payment";
import { CLIENT_URLS } from "constants/urls";
import { doRedirect } from "helpers/utils/redirection";
import { toLowerCase } from "helpers/utils/toLowerCase";
import { IPaymentFallbackModes } from "layouts/PaymentFailure/@types";
import { isAndroid, isIos } from "helpers/deviceType";
import { isServer } from "helpers/utils/isServer";
import { formatNumber } from "helpers/utils/numberFormatter";

/**
 * @description
 * function that gets vpa details from upiMapping
 * @param vpaHandle
 * @param upiMapping
 */
export const getVpaDetails = (
  vpaHandle: string,
  upiMapping: [
    {
      title: string;
      iconIntentUrl: string;
      iconNotifyUrl: string;
      completeIcon: string;
      notifyMessage: string;
      vpaSuffix: [string];
      package: string;
    }
  ]
) => {
  let vpaDetails = {
    title: "BHIM UPI",
    vpa: vpaHandle || "",
    icon: "https://adn-static1.nykaa.com/media/wysiwyg/UPI_Icons/UPI.svg",
    notifyIcon: "https://adn-static1.nykaa.com/media/wysiwyg/UPI_Icons/Notify.svg",
    completeIcon: "https://adn-static1.nykaa.com/media/wysiwyg/UPI_Icons/Done.svg",
    message: "Check for “Payment Request” from <b>FSN BRANDS MARKETING LIMITED</b>",
    packageName: "",
  };

  let response = {
    showSavedVpa: false,
    ...vpaDetails,
  };

  if (Array.isArray(upiMapping) && upiMapping.length) {
    upiMapping.forEach(upiMappingValue => {
      const {
        title,
        vpaSuffix,
        iconIntentUrl,
        iconNotifyUrl,
        completeIcon,
        notifyMessage,
      } = upiMappingValue;

      if (vpaHandle) {
        const userVpaSuffix = vpaHandle.substr(vpaHandle.indexOf("@") + 1);
        if (vpaSuffix.includes(userVpaSuffix)) {
          vpaDetails = {
            title,
            vpa: vpaHandle,
            icon: iconIntentUrl,
            notifyIcon: iconNotifyUrl,
            completeIcon,
            message: notifyMessage,
            packageName: upiMappingValue?.package, // we are getting error, package is a reserved keyword.
          };

          response = {
            showSavedVpa: true,
            ...vpaDetails,
          };
        }
      }
    });
  }
  return response;
};

/**
 *
 * @param upiMode selected upi from upi icons in new upi layout
 * @returns list of vpa handle suffix
 * @description remoteConfig: {
 * ...
 *  configs: {
 *    ...
 *    payment: {
 *      vpaSuffixList: {
 *        "phonePe": ["@ybl", "@axl", "@ibl","Other UPI"],
 *        "googlePay": ["@okicici", "@oksbi","okaxis","@okhdfcbank","Other UPI"],
 *        "paytm": ["@paytm"],
 *      }
 *    }
 *    ...
 * }
 * ...
 * }
 */

export const getVpaSuffixList = (upiMode: string) => {
  let vpaSuffixList = []; //'@ybl', '@axl'
  try {
    if (!isServer()) {
      const { remoteConfig } = window as any;
      const list = remoteConfig?.configs?.payment?.vpaSuffixList?.[upiMode];
      if (list) {
        vpaSuffixList = list;
      }
    }
  } catch (error) {
    logger.error(error);
  }

  return vpaSuffixList;
};

export const getTransformedVpaList = (vpaSuffixList: string[]) => {
  try {
    if (!isServer()) {
      const finalVpaSuffixList = vpaSuffixList.map((value) => ({
        text: value,
      }));
      return finalVpaSuffixList;
    }
  } catch (error) {
    logger.error(error);
  }
  return vpaSuffixList;
};


/**
 * @description
 * function to get upi payment detail
 * @param upiMapping
 * @param paymentMethod
 */
export const getUpiPaymentDetail = (upiMapping: [{
  title: string, iconIntentUrl: string,
  iconNotifyUrl: string,
  notifyMessage: string,
}], paymentMethod: string) => {
  let response = {
    title: "BHIM UPI",
    intentIcon: "",
    notifyIcon: "",
    notifyMessage: "",
  };

  if (Array.isArray(upiMapping)) {
    upiMapping.forEach((upiMappingValue) => {
      const {
        title,
        iconIntentUrl,
        iconNotifyUrl,
        notifyMessage,
      } = upiMappingValue;

      if (paymentMethod === title) {
        response = {
          title,
          intentIcon: iconIntentUrl,
          notifyIcon: iconNotifyUrl,
          notifyMessage,
        };
      }
    });
  }
  return response;
};

/**
 *
 * @param selectedUpi selected upi from the list of UPI Icons
 * @param vpa selected vpa suffix from drop down
 * @returns transformed vpa
 */

export const validateInputOnVpaChange = (selectedUpi: string, vpa: string) => {
  if (selectedUpi == PAYMENT_METHODS.UPI) {
    return vpa;
  } else if (vpa && vpa.includes("@")) {
    let newVpa = vpa.substring(0, vpa.indexOf("@"));
    return newVpa;
  }

  return vpa;
};


/**
 * @description
 * function that checks remote config value for disabled payment method
 * @param paymentMode
 * @returns
 * object containing forMobile, forDesktop
 */
export const getDisabledPaymentMethods = (paymentMode: string) => {
  let enabledForDesktop = false;
  let enabledForMobile = false;
  if (!isServer()) {
    const { remoteConfig } = window as any;
    const enabledGooglePayGlobally = remoteConfig?.switches?.[paymentMode]?.status;
    if (enabledGooglePayGlobally) {
      enabledForMobile = remoteConfig?.switches?.[paymentMode]?.configs?.enableMobile;
      enabledForDesktop = remoteConfig?.switches?.[paymentMode]?.configs?.enableDesktop;
    }
  }
  return {
    forMobile: enabledForMobile,
    forDesktop: enabledForDesktop,
  };
};

/**
 * @description
 * generates message on offer
 * based on discountPercent or discountAmount
 * @param offersData - offers object from offersContext
 * @returns offerMessage
 */
export const getOfferMessage = (offersData: OffersDataType) => {
  let offerMessage = "";
  if (offersData && offersData?.hasOffers) {
    if (offersData.discountAmount && offersData.discountAmount > 0) {
      offerMessage = getStringFromId(
        VERNACULAR_STRING_IDS.DYNAMIC_DISCOUNT_OFF,
        `₹${formatNumber(offersData.discountAmount)} OFF`,
        { count: `${offersData.discountAmount}` },
      );
    }
    else if (offersData.discountPercent && offersData.discountPercent > 0) {
      offerMessage = getStringFromId(
        VERNACULAR_STRING_IDS.DYNAMIC_PERCENTAGE_OFF,
        `${offersData.discountPercent}% OFF`,
        { count: `${offersData.discountPercent}` },
      );
    }
  }
  return offerMessage;
};

/**
 * @description
 * generates message on offer
 * based on discountPercent or discountAmount
 * @param offersData - offers object from offersContext
 * @returns offerMessage
 */
export const getUpToOfferMessage = (offersData: OffersDataType) => {
  let offerMessage = "";
  if (offersData && offersData.hasOffers) {
    if (offersData.discountPercent && offersData.discountPercent > 0) {
      offerMessage = getStringFromId(
        VERNACULAR_STRING_IDS.DYNAMIC_UP_TO_PERCENTAGE_OFF,
        `UP TO ${offersData.discountPercent}% OFF`,
        { count: `${offersData.discountPercent}` },
      );
    } else if (offersData.discountAmount && offersData.discountAmount > 0) {
      offerMessage = getStringFromId(
        VERNACULAR_STRING_IDS.DYNAMIC_UP_TO_DISCOUNT_OFF,
        `UP TO ₹${formatNumber(offersData.discountAmount)} OFF`,
        { count: `${offersData.discountAmount}` },
      );
    }
  }
  return offerMessage;
};

/**
 * @description
 * function that converts array to hashMap using key
 * @param arr - array to be coverted
 * @param key - key value for hashMap
 */
export const convertArrayToHashMap = (arr: [], key: string) => {
  if (Array.isArray(arr)) {
    const hashMap = {};
    arr.forEach((value) => {
      if (value[key]) {
        hashMap[value[key]] = value;
      }
    });
    return hashMap;
  }
  return arr;
};


/**
 * @description - countryNameSelected return country name mapped to the countryCode
 * used to display the selected name
 */
export const countryNameSelected = (countryCode: string) => {
  switch (countryCode) {
    case "IN":
      return "India";
    case "NP":
      return "Nepal";
    case "LK":
      return "Sri Lanka";
    default:
      return "";
  }
};

/**
 * @description
 * getCountryMeta function returns meta data for the country like DIAL_CODE, CODE, NAME of the country.
 * @param countryCode code for the country like India - IN, etc
 */

export const getCountryMeta = (countryCode: string) => {
  if (countryCode) {
    if (countryCode === "IN") {
      return { NAME: "India", DIAL_CODE: "+91", CODE: "IN" };
    } else if (countryCode === "NP") {
      return { NAME: "Nepal", DIAL_CODE: "+977", CODE: "NP" };
    } else if (countryCode === "LK") {
      return { NAME: "Sri Lanka", DIAL_CODE: "+94", CODE: "LK" };
    }
  }

  return { NAME: "", DIAL_CODE: "", CODE: "" };
};


export const getUtmSourcePaymentTag = () => {
  try {
    const utmTag = getCookie("utm_source");
    if (utmTag) {
      return utmTag;
    }
  } catch (error) {
    // ravenInstance.logError(new Error(error), 'error in checking utm params is set');
  }
  return null;
};


// function that masks middle 4 digits of mobileNumber
export const getMaskedMobileNumber = (number: number) => {
  try {
    let maskedString = "";
    if (!number) {
      return maskedString;
    }
    maskedString = number.toString().slice(0, 3) + "xxxx" + number.toString().slice(7, 10);
    return maskedString;
  } catch (error) {
    logger.error(error);
  }
};


/**
 * @description
 * checkIsZestMoneyEnabled checks whether experiment is enabled by validating the cookie
 * and passing which variant the experiment falls under
 * @returns
 * object with a boolean variable isZestMoneyVisible and zestVariant which is to be passed in tracking
 */

// export const checkIsZestMoneyEnabled = () => {
//   let isZestMoneyVisible = true;
//   let zestVariant = '';
//   if (!isServer()) {
//     let zestMoneyVariant1Cookie = getCookie(ZEST_MONEY_EXPERIMENT.VARIANT_1);
//     let zestMoneyVariant2Cookie = getCookie(ZEST_MONEY_EXPERIMENT.VARIANT_2);
//     let zestMoneyVisibileCookie = getCookie(ZEST_MONEY_EXPERIMENT.VISIBILITY);
//     if (zestMoneyVariant1Cookie === 'true') {
//       zestVariant = ZEST_MONEY_EXPERIMENT.VARIANT_1;
//     } else if (zestMoneyVariant2Cookie === 'true') {
//       zestVariant = ZEST_MONEY_EXPERIMENT.VARIANT_2;
//     } else if (zestMoneyVisibileCookie === 'true') {
//       zestVariant = ZEST_MONEY_EXPERIMENT.VISIBILITY;
//       isZestMoneyVisible = true;
//     }
//   }
//   return {
//     isZestMoneyVisible,
//     zestVariant,
//   };
// };


/**
* @param {object} cartInfo: contains information of the cart
* @description
* Function to get the payment tags which came from the cart API.
* It could be an array of string or null.
* @returns {string} return the payment tags string with comma seperated.
*/
export const getPaymentTagsString = (paymentTags: string[]) => {
  try {
    return Array.isArray(paymentTags) ? paymentTags.join() : "";
  } catch (err) {
    logger.error(err, "payment tags should be an array or null");
  }
  return "";
};


// return the value of the pincode entered by the user on cart or pdp
export const getPincodeValue = () => {
  let isAddressMatchedWithPincodeValue = "";
  try {
    isAddressMatchedWithPincodeValue = getCookie("pinCodeDel") as string;
    if (isAddressMatchedWithPincodeValue) {
      isAddressMatchedWithPincodeValue = JSON.parse(isAddressMatchedWithPincodeValue);
    }
  } catch (error) {
    logger.error(error as Error);
  }

  return isAddressMatchedWithPincodeValue;
};

// If addressIdDel cookie is not present, we are considering the case as anonymous pin code. Function returns true and display the message bar on the top of the checkoutPage
export const checkIsAddressIdDelCookiePresent = () => {
  let isAddressIdDelCookiePresent = false;

  let isAddressMatchedWithPincodeValue = getCookie("addressIdDel");

  if (isAddressMatchedWithPincodeValue) {
    isAddressIdDelCookiePresent = true;
  }

  return isAddressIdDelCookiePresent;
};

export const getPaymentFlowAndWhatsAppToken = () => {
  const response = {
    paymentFlow: "",
    codToPrepaidWhatsAppToken: "",
  };

  try {
    const paymentFlowStorage = localStorageService.getItem("PAYMENT_FLOWS");
    if (paymentFlowStorage) {
      const codToPrepaidWhatsAppTokenStorage = localStorageService.getItem("COD_TO_PREPAID_WHATSAPP_TOKEN");
      response.paymentFlow = paymentFlowStorage;
      if (codToPrepaidWhatsAppTokenStorage) {
        response.codToPrepaidWhatsAppToken = codToPrepaidWhatsAppTokenStorage;
      }
    }
  } catch (e) {
    return response;
  }

  return response;
};

/**
 * @description
 * function that handles user redirection to payment preconfirmation page
 * if new payment preconfirmation redirection is true, redirected to new stack payment preconfirmation page
 * redirection to old payment preconfirmation url in other cases
 * @param {*} queryParams - orderId parameter to be appended
 */
 export const redirectToPaymentPreConfirmationPage = (
   history: any,
   queryParams: string,
 ) => {
   try {
     if (!isServer()) {
       let url = CLIENT_URLS.PAYMENT_PRE_CONFIRMATION_PAGE;

       if (queryParams) {
         url = `${url}?${queryParams}`;
       }

       doRedirect({
         url,
         isClientRedirection: true,
         history,
       });
     }
   } catch (error) {
     logger.error(error);
   }
 };

/**
 * @description
 * Function to get the avilable payment offers from paymentMethod Api.
 * @returns {string} returns array of offer rule ID's.
 */
 export const getPaymentOffers = (offers: any) => {
  const paymentOffers = offers?.map((offer: any) => offer.ruleId) || [];
  return paymentOffers;
 };

/**
 * This function is only used for fashion and can be removed
 * in future once experiment is rolled out at 100%.
 * https://nykmage.atlassian.net/browse/FC-2293
 *
 * @returns boolean
 */
 export const checkIsRewardPointsEnabled = (): boolean => {
  let isEnabled = false;
  try {
    isEnabled = sessionStorage.getItem("nykaa_show_onr") == "true"; // true will be saved as string
  } catch (error) {
    logger.error(error as Error);
  }
  return isEnabled;
};

/**
 * @description
 * Function to get the schedule intent notification from remoteConfig.
 * @returns {object} .
 */
export const getScheduleIntentNotificationConfig = () => {
  let config = {};
  try {
    if (!isServer()) {
      const { remoteConfig } = window as any;
      const intentNotification = remoteConfig?.configs?.intentNotification;
      if (intentNotification) {
        config = intentNotification;
      }
    }
  } catch (error) {
    logger.error(error);
  }

  return config;
};

/**
 * @description -- function to check that web retina events will be enabled through code or not
 */
 export const checkIsRetinaEventEnabledByCode = () => {
   let codeRetinaEventTringer = false;
   try {
     if (!isServer()) {
       const { remoteConfig } = window as any;
       codeRetinaEventTringer = remoteConfig?.switches?.retinaEventsMigration?.status;
       return codeRetinaEventTringer;
     }
   } catch (error) {
     logger.error(error as Error);
   }
   return codeRetinaEventTringer;
 };

/**
 * getPaymentFailureMessageUsingPaymentMode - function to calculate the payment option to be shown
 * on payment failure bottomsheet
 */
 export const getPaymentFailureDataUsingPaymentMode = (
   fallbackModes: IPaymentFallbackModes,
   masterPaymentStatus: string,
   prevPaymentMethod: string,
 ) => {
     const fallBackData = fallbackModes?.[toLowerCase(masterPaymentStatus)];
     const fallbackMessages = fallBackData?.fallbackMessages;
     const nonFallbackMessages = fallBackData?.nonFallbackMessages;
     const fallbackPaymentOptions = fallBackData?.modes?.[toLowerCase(prevPaymentMethod)];
     return { fallbackMessages, nonFallbackMessages, fallbackPaymentOptions };
 };

 /**
 * This function will be used to show header on third party domains in webview.
 * @returns string
 */
export const getHeaderTitleForAppsOnThirdPartyDomain = () => {
  switch (__PLATFORM__) {
    case platformNames.FASHION:
      return "Nykaa Fashion";
    case platformNames.FOOTLOCKER:
      return "Footlocker";
    case platformNames.MEN:
      return "Nykaa Man";
    case platformNames.NYKAA:
      return "Nykaa";
    default:
      return "Nykaa";
  }
};

/**
 * @description -- function to fetch pollingDuration from remoteConfig
 * for paymentFailureFlow
 * (time after  which api(/payment/response/v2) need to be hitted again)
 */
 export const getPaymentFailurePollingDuration = () => {
  let pollingDuration = 2000;
  try {
    if (!isServer()) {
      const { remoteConfig } = window as any;
      pollingDuration = remoteConfig?.switches?.newPaymentFailure?.pollingDuration || 2000;
    }
  } catch (error) {
    logger.error(error, "Error in getPaymentFailurePollingDuration");
  }

  return pollingDuration;
};
 /**
 * @returns upi intent polling time in second fetch from remoteConfig o/w will return default time.
 *
 * Remote config data format.
 *
 * {
 *  "configs": {
 *     "payment": {
 *       "upiIntentPollingInSec": {
 *         "android": 120,
 *         "ios": 30
 *       }
 *     }
 *   }
 * }
 */

export const getUpiIntentPollingTime = () => {
  // Set default upi polling time if we won't be able to fetch the timer from remoteConfig.
  const DEFAULT_POLLING_TIME_IN_SEC = {
    android: 0,
    ios: 30,
  };
  let upiIntentPollingTimeInSec = DEFAULT_POLLING_TIME_IN_SEC.ios;
  if (isAndroid()) {
    upiIntentPollingTimeInSec = DEFAULT_POLLING_TIME_IN_SEC.android;
  }

  // Fetch upi polling time from remoteConfig
  try {
    if (!isServer()) {
      const { remoteConfig } = window as any;
      if (isAndroid()) {
        upiIntentPollingTimeInSec = remoteConfig?.configs?.payment?.upiIntentPollingInSec?.android;
      } else if (isIos()) {
        upiIntentPollingTimeInSec = remoteConfig?.configs?.payment?.upiIntentPollingInSec?.ios;
      }
      return upiIntentPollingTimeInSec;
    }
  } catch (error) {
    logger.error(error as Error);
  }
  return upiIntentPollingTimeInSec;
};

/**
 * This function is to switch to the first new payment method
 * if user is not eligible for any method
 * if cod on first place or current payment itself on first place
 * then show second payment option to user.
 */
export const doHandleOtherPaymentClick = ({
  deviceType,
  paymentMethodDesktop,
  paymentMethodMsite,
  callBackForDesktop,
  callBackForMsite,
  currentPaymentOption,
  isDisabled,
}: any) => {
  if (deviceType === DEVICE_TYPE.DESKTOP) {
    if (paymentMethodDesktop?.length > 0) {
      let firstOption = paymentMethodDesktop[0];
      if (
        (firstOption === ACCORDIAN.COD || firstOption === currentPaymentOption) &&
        paymentMethodDesktop.length > 1
      ) {
        callBackForDesktop(paymentMethodDesktop[1], isDisabled);
      } else {
        callBackForDesktop(firstOption, isDisabled);
      }
    }
  } else {
    const paymentMethodlength = paymentMethodMsite?.length;
    if (paymentMethodlength > 0) {
      if (
        (paymentMethodMsite[0]?.method === ACCORDIAN.COD ||
          paymentMethodMsite[0]?.method === currentPaymentOption) &&
        paymentMethodlength > 1
      ) {
        callBackForMsite(paymentMethodMsite[1]?.method, isDisabled);
      } else {
        callBackForMsite(paymentMethodMsite[0]?.method, isDisabled);
      }
    }
  }
};
